/**
 * Responsibility: keep a page's view state across a navigation away and back.
 *
 * Every page is a route component, so switching tabs unmounts it and every
 * `useState` on it goes back to its default. The data survives - react-query
 * caches that - but the view does not: the search page forgot the query that
 * produced the results still sitting in the cache, and the discovery board
 * forgot which era, floor and language was being looked at.
 *
 * This is a `useState` that remembers. Same signature, one extra argument:
 * a key that has to be unique across the app, because the store is flat.
 *
 * **Two lifetimes, chosen per value.** A filter is a preference and should
 * still be set tomorrow, so it goes to `localStorage`. A query, a scroll
 * position or an open dialog belongs to the sitting and should not greet
 * someone a week later, so those go to `sessionStorage`. That split is the
 * whole reason this takes an option rather than picking one for everything.
 *
 * An in-memory mirror sits in front of both. Reading a route's state should
 * not touch storage on every render, and the mirror is also what makes this
 * work at all for values that fail to serialise.
 *
 * **Every key is scoped to the signed-in person.** These are not innocuous
 * view preferences: `discovery.liked` is a list of product keys, and the open
 * dialog states serialise whole holdings and search results into storage. On a
 * shared browser an unscoped store hands all of that to whoever signs in next.
 * The mirror is cleared on a change of owner as well, because it is not
 * storage-backed and would otherwise outlive the switch for the rest of the
 * page's life.
 */
import { useCallback, useEffect, useState } from 'react';

/** How long a remembered value should outlive the page. */
export type StateLifetime = 'session' | 'durable';

/** Namespaced so a key cannot collide with anything else in storage. */
const PREFIX = 'dropmon.page.';

/**
 * Whose state is being read and written.
 *
 * Module-level rather than a hook argument: every one of the twenty-four call
 * sites would otherwise have to pass it, and any that forgot would silently
 * write to the shared namespace - a failure that looks like nothing at all
 * until two people use one browser.
 */
let owner = 'anon';

function scoped(key: string): string {
  return `${PREFIX}${owner}.${key}`;
}

/**
 * Read in front of storage, and the fallback when storage is unavailable
 * or the value will not serialise.
 */
const mirror = new Map<string, unknown>();

function store(lifetime: StateLifetime): Storage | null {
  try {
    return lifetime === 'durable' ? window.localStorage : window.sessionStorage;
  } catch {
    // Private browsing and some embedded webviews throw on access rather
    // than returning null. The mirror covers it.
    return null;
  }
}

function read<T>(key: string, lifetime: StateLifetime, fallback: T): T {
  const full = scoped(key);
  if (mirror.has(full)) return mirror.get(full) as T;

  const raw = store(lifetime)?.getItem(full);
  if (raw === null || raw === undefined) return fallback;
  try {
    const parsed = JSON.parse(raw) as T;
    mirror.set(full, parsed);
    return parsed;
  } catch {
    // A value written by an older build whose shape has since changed.
    // Treated as absent rather than allowed to crash the page.
    return fallback;
  }
}

function write<T>(key: string, lifetime: StateLifetime, value: T): void {
  const full = scoped(key);
  mirror.set(full, value);
  try {
    store(lifetime)?.setItem(full, JSON.stringify(value));
  } catch {
    // Quota, or a value with a cycle in it. The mirror still holds it, so
    // the page keeps working for this sitting.
  }
}

/**
 * `useState` that survives leaving the page.
 *
 * The initial value is only used the first time a key is ever seen; after
 * that the remembered value wins, which is the point.
 */
export function usePageState<T>(
  key: string,
  initial: T,
  lifetime: StateLifetime = 'session',
): [T, (next: T | ((current: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => read(key, lifetime, initial));

  // Written in an effect rather than inside the setter so that a value
  // changed by anything else - a reset, a parent re-keying - is still
  // persisted, and so the write never happens during a render.
  useEffect(() => {
    write(key, lifetime, value);
  }, [key, lifetime, value]);

  const set = useCallback((next: T | ((current: T) => T)) => {
    setValue((current) =>
      typeof next === 'function' ? (next as (c: T) => T)(current) : next,
    );
  }, []);

  return [value, set];
}

/**
 * Forget one remembered value.
 *
 * Exported for the case a page needs an explicit "start again" rather than
 * for routine use.
 */
export function forgetPageState(key: string, lifetime: StateLifetime = 'session'): void {
  const full = scoped(key);
  mirror.delete(full);
  try {
    store(lifetime)?.removeItem(full);
  } catch {
    // Nothing to do: the mirror is already clear.
  }
}

/**
 * Point the store at a different person.
 *
 * Clears the in-memory mirror on a real change. Stored values are left where
 * they are: they sit under that person's own prefix, so they are both safe
 * from the next user and still there when their owner returns.
 */
export function setPageStateOwner(uid: string | null): void {
  const next = uid ?? 'anon';
  if (next === owner) return;
  owner = next;
  mirror.clear();
}
